import React from 'react'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'
import { IAddOnProgram, IEpiProps, IProgramBase, ISpecialtyProgram, StepConstants } from '../models/event-reg-refresh-models'
import { CardAccordion } from './card-accordion'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
}

interface IProps {
  program: ISpecialtyProgram | IAddOnProgram;
  isSelected?: boolean;
  programType: 'specialty' | 'addon';
  showPrice: boolean;
  currency: string;
  onSelect: (program: ISpecialtyProgram) => void;
  onCheckRenewalEligibility?: (programCode: string) => void; //Only applies to specialty program renewals
}

export const ProgramCard = (props: IProps) => {
  const { epiProps, currentStep }: IContextProps = useEventRegRefreshContext()!

  // Check if program is a SpecialtyProgram
  const isSpecialtyProgram = (prog: IProgramBase): prog is ISpecialtyProgram => {
    return props.programType === 'specialty';
  }

  const isAddOnProgram = (program: any): program is IAddOnProgram => {
    return 'programChips' in program;
  }

  const program = props.program;
  /* Use to debug program data */
  //  React.useEffect(() => {
  //   console.log('Program data:', JSON.stringify(program, null, 2));
  // }, [program]);
  let selectButtonText =
    props.isSelected && props.isSelected == true ? epiProps.selectedButtonText : epiProps.selectButtonText

  return (
    <div className={`card ${props.isSelected && props.isSelected == true ? 'card-selected' : ''
      }`}>
      <div className="card-header">
        {program.imageUrl && program.imageUrl.length > 0 && (
          <img className="card-header-image" src={program.imageUrl} alt={program.programImageAlternateText} />
        )}
        <div className="card-header-content">

          <h3 className="card-header-title">
            {program.programName}
          </h3>
          {props.showPrice &&
            <div className="card-legend-item">
              <span className="card-legend-item-icon">
                <svg className="svg-icon" aria-hidden="true" focusable="false">
                  <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#dollar-icon" />
                </svg>
              </span>
              <p className="h6-style app-sort">
                {epiProps.costPerAttendeeLabelText}: ${program.programPrice.toLocaleString()} {props.currency}
                {/* Display X-Year Certification if applicable */}
                {isSpecialtyProgram(program) && typeof program.xYearCertification === 'number' && program.xYearCertification > 0 && (
                  <><span>({program.xYearCertification}-Year Certification)</span></>
                )}
              </p>
            </div>
          }
        </div>
        <div className="card-header-actions">
          {
            isSpecialtyProgram(program) && program.isRenewal &&
            <button
              onClick={() => props.onCheckRenewalEligibility!(program.programCode)}
              className="btn-tertiary">{epiProps.checkRenewalEligibilityCtaText}</button>
          }

          <button className="btn-primary card-action-button" onClick={props.onSelect}>{selectButtonText}
            {props.isSelected && <svg className="svg-icon" aria-hidden="true" focusable="false">
              <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#icon-checkmark" />
            </svg>}
          </button>
        </div>
      </div>
      {/*Chips for Add-Ons*/}
      {isAddOnProgram(program) && program.programChips && program.programChips.length > 0 &&
        <div className="card-chip-container">
          {
            program.programChips.map((chip) => <span className="card-chip-item">{chip}</span>)
          }
        </div>
      }

      {isSpecialtyProgram(program) && (
        <div>
          {/* Chips */}
          <div className="card-chip-container">
            {program.industries && program.industries.length > 0 && program.industries.map((i) => <span className="card-chip-item">{i}</span>)}
            {program.programFormats &&
              program.programFormats.length > 0 &&
              program.programFormats.map((pf) => <span className="card-chip-item">{pf}</span>)}
            {program.trainingTypes &&
              program.trainingTypes.length > 0 &&
              program.trainingTypes.map((t) => <span className="card-chip-item">{t}</span>)}
          </div>

          {/* Comparison */}
          <div className="card-split-layout">
            <div>
              {program.mainFeatures && program.mainFeatures.length > 0 && (
                <div>
                  <p><strong>{epiProps.mainFeaturesHeaderText}</strong></p>
                  <ul>
                    {program.mainFeatures.map((feature, index) => (
                      <li className="card-feature-list-item" key={index}><svg className="svg-icon" aria-hidden="true" focusable="false">
                        <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#icon-checkmark" />
                      </svg><span>{feature}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              {program.additionalFeatures && program.additionalFeatures.length > 0 && (
                <div>
                  <p><strong>{epiProps.additionalFeaturesHeaderText}</strong></p>
                  <ul>
                    {program.additionalFeatures.map((feature, index) => (
                      <li className="card-feature-list-item" key={index}><svg className="svg-icon" aria-hidden="true" focusable="false">
                        <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#icon-plus" />
                      </svg>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/*Program Details accordion*/}
          {
            program.showProgramDetailsOutsideAccordion === false &&
            <CardAccordion title={epiProps.programDetailsHeaderText} includeSnippet>
              <div className="rich-text" dangerouslySetInnerHTML={{ __html: program.programDetails }} />
              <div className="card-split-layout">
                <div className="rich-text" dangerouslySetInnerHTML={{ __html: program.programDetailsLeft }} />
                <div className="rich-text" dangerouslySetInnerHTML={{ __html: program.programDetailsRight }} />
              </div>
            </CardAccordion>
          }

          {/*Some cards will show program details outside the accordion.*/}
          {
            program.showProgramDetailsOutsideAccordion &&
            <div className="rich-text" dangerouslySetInnerHTML={{ __html: program.programDetails }} />
          }

        </div>
      )}

      {!isSpecialtyProgram(program) && (
        <>
          <div dangerouslySetInnerHTML={{ __html: program.programDetails }} />
          {(program as IAddOnProgram).detailsDescription && (
            <div className="card-addon-details">
              <CardAccordion title={epiProps.addOnDetailsHeaderText} includeSnippet>
                <div className="rich-text" dangerouslySetInnerHTML={{ __html: (program as IAddOnProgram).detailsDescription }} />
              </CardAccordion>
            </div>
          )}
        </>
      )}
    </div>
  )
}
